import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { getCandidates, createCandidate } from '../services/api';
import { Users, Plus, Search, Phone, MapPin, X, Briefcase } from 'lucide-react';
import toast from 'react-hot-toast';

const statusColor = (s) =>
  s === 'active'    ? 'bg-green-50 text-green-700' :
  s === 'deployed'  ? 'bg-blue-50 text-blue-700' :
  s === 'on_bench'  ? 'bg-amber-50 text-amber-700' :
  s === 'inactive'  ? 'bg-gray-50 text-gray-500' :
  'bg-red-50 text-red-600';

const categoryColor = (c) =>
  c === 'blue_collar'  ? 'bg-blue-50 text-blue-700' :
  c === 'white_collar' ? 'bg-purple-50 text-purple-700' :
  c === 'specialized'  ? 'bg-teal-50 text-teal-700' :
  'bg-orange-50 text-orange-700';

const AddCandidateModal = ({ onClose }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    first_name: '', last_name: '', phone: '',
    email: '', city: '', state: '',
    worker_category: 'blue_collar',
    total_experience: 0, source: 'walk_in'
  });

  const mutation = useMutation({
    mutationFn: createCandidate,
    onSuccess: () => {
      toast.success('Candidate added successfully!');
      queryClient.invalidateQueries(['candidates']);
      onClose();
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to add candidate')
  });

  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    mutation.mutate(form);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 max-h-screen overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-gray-900">Add New Candidate</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
              <input name="first_name" value={form.first_name} onChange={handle} required
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g. Ravi" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
              <input name="last_name" value={form.last_name} onChange={handle}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g. Kumar" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
              <input name="phone" value={form.phone} onChange={handle} required
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="9xxxxxxxxx" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input name="email" value={form.email} onChange={handle}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="ravi@email.com" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
              <input name="city" value={form.city} onChange={handle}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g. Puducherry" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
              <input name="state" value={form.state} onChange={handle}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g. Tamil Nadu" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Worker Category *</label>
              <select name="worker_category" value={form.worker_category} onChange={handle}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="blue_collar">Blue Collar</option>
                <option value="white_collar">White Collar</option>
                <option value="specialized">Specialized</option>
                <option value="technical">Technical</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Experience (years)</label>
              <input name="total_experience" type="number" value={form.total_experience} onChange={handle}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="0" max="50" step="0.5" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
            <select name="source" value={form.source} onChange={handle}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="walk_in">Walk-in</option>
              <option value="referral">Referral</option>
              <option value="job_board">Job Board</option>
              <option value="portal">Portal</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={mutation.isPending}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
              {mutation.isPending ? 'Saving...' : 'Add Candidate'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Candidates = () => {
  const navigate = useNavigate();
  const [search,   setSearch]   = useState('');
  const [status,   setStatus]   = useState('');
  const [category, setCategory] = useState('');
  const [showAdd,  setShowAdd]  = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['candidates', search, status, category],
    queryFn : () => getCandidates({ search, status, worker_category: category, limit: 20 }).then(r => r.data)
  });

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Candidates</h1>
          <p className="text-sm text-gray-400 mt-0.5">Manage your worker profiles</p>
        </div>
        <button onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700">
          <Plus size={16} />
          Add Candidate
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, phone or employee code..."
            className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <select value={status} onChange={e => setStatus(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="deployed">Deployed</option>
          <option value="on_bench">On Bench</option>
          <option value="inactive">Inactive</option>
        </select>
        <select value={category} onChange={e => setCategory(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">All Categories</option>
          <option value="blue_collar">Blue Collar</option>
          <option value="white_collar">White Collar</option>
          <option value="specialized">Specialized</option>
          <option value="technical">Technical</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        {isLoading ? (
          <div className="text-center py-12 text-gray-400">Loading candidates...</div>
        ) : data?.data?.length === 0 ? (
          <div className="text-center py-12">
            <Users size={32} className="mx-auto text-gray-200 mb-3" />
            <p className="text-gray-400 text-sm">No candidates yet. Add your first candidate!</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">Candidate</th>
                <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">Phone</th>
                <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">Category</th>
                <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">Location</th>
                <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">Experience</th>
                <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {data?.data?.map(c => (
                <tr key={c.id} className="hover:bg-gray-50 transition cursor-pointer"
  onClick={() => navigate(`/candidates/${c.id}`)}>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-purple-50 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-purple-600 text-xs font-semibold">
                          {c.first_name?.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {c.first_name} {c.last_name}
                        </p>
                        <p className="text-xs text-gray-400">{c.employee_code}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-1 text-sm text-gray-600">
                      <Phone size={13} className="text-gray-400" />
                      {c.phone}
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${categoryColor(c.worker_category)}`}>
                      {c.worker_category?.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-1 text-sm text-gray-600">
                      <MapPin size={13} className="text-gray-400" />
                      {c.city || '—'}
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-1 text-sm text-gray-600">
                      <Briefcase size={13} className="text-gray-400" />
                      {c.total_experience ? `${c.total_experience} yrs` : '—'}
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${statusColor(c.status)}`}>
                      {c.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showAdd && <AddCandidateModal onClose={() => setShowAdd(false)} />}
    </div>
  );
};

export default Candidates;